// Pure local search index (STORE01 worker indexing, NFR02). Runs in search.worker.ts, or in-thread in chunks as a fallback.

export type IndexItem = { id: string; kind: string; title: string; categoryIds?: string[] };
/** rank: 0 = title starts with the query, 1 = every query word prefixes a title word, 2 = title contains the query. */
export type Hit = { id: string; kind: string; title: string; rank: 0 | 1 | 2 };
export type QueryOptions = { limit?: number; kinds?: string[]; excludeCategoryIds?: string[] };

const PREFIX = 3;

/** NFKD, strip marks, lowercase, non-alphanumerics to single spaces, trim. */
export function fold(s: string): string {
  return s.normalize('NFKD').replace(/\p{M}+/gu, '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
}

export type SearchIndex = {
  ids: string[]; kinds: string[]; titles: string[]; folded: string[]; categories: (string[] | undefined)[];
  byId: Map<string, number>;
  /** first 1..3 characters of every title word → item positions */
  postings: Map<string, number[]>;
};

export const createIndex = (): SearchIndex => ({ ids: [], kinds: [], titles: [], folded: [], categories: [], byId: new Map(), postings: new Map() });

export function addItems(ix: SearchIndex, items: readonly IndexItem[]): void {
  for (const it of items) {
    const f = fold(it.title);
    let i = ix.byId.get(it.id);
    const known = i !== undefined;
    if (i === undefined) {
      i = ix.ids.length;
      ix.byId.set(it.id, i);
      ix.ids.push(it.id);
    }
    const unchanged = known && ix.folded[i] === f;
    ix.kinds[i] = it.kind;
    ix.titles[i] = it.title;
    ix.folded[i] = f;
    ix.categories[i] = it.categoryIds;
    if (unchanged) continue;
    for (const word of f.split(' ')) {
      for (let n = 1; n <= Math.min(PREFIX, word.length); n++) {
        const p = word.slice(0, n);
        const list = ix.postings.get(p);
        if (!list) ix.postings.set(p, [i]);
        else if (list[list.length - 1] !== i) list.push(i);
      }
    }
  }
}

function rankOf(title: string, q: string, words: string[]): -1 | 0 | 1 | 2 {
  if (title.startsWith(q)) return 0;
  const padded = ' ' + title;
  if (words.every((w) => padded.includes(' ' + w))) return 1;
  return title.includes(q) ? 2 : -1;
}

/** Ranked and deterministic: rank, then shorter title, then title, then id. */
export function query(ix: SearchIndex, q: string, o: QueryOptions = {}): Hit[] {
  const qf = fold(q);
  if (!qf) return [];
  const limit = o.limit ?? 50;
  const kinds = o.kinds?.length ? new Set(o.kinds) : null;
  const excluded = o.excludeCategoryIds?.length ? new Set(o.excludeCategoryIds) : null;
  const words = [...new Set(qf.split(' '))];
  const allowed = (i: number) => (!kinds || kinds.has(ix.kinds[i])) && (!excluded || !ix.categories[i]?.some((c) => excluded.has(c)));

  // Rank 0/1 matches carry every query word as a word prefix, so the smallest posting list holds all of them.
  let candidates: number[] = [];
  words.forEach((w, n) => {
    const list = ix.postings.get(w.slice(0, PREFIX)) ?? [];
    if (n === 0 || list.length < candidates.length) candidates = list;
  });
  const seen = new Set<number>();
  const hits: { i: number; rank: 0 | 1 | 2 }[] = [];
  for (const i of candidates) {
    if (seen.has(i)) continue;
    seen.add(i);
    if (!allowed(i)) continue;
    const rank = rankOf(ix.folded[i], qf, words);
    if (rank >= 0) hits.push({ i, rank: rank as 0 | 1 | 2 });
  }
  // Mid-word "contains" matches need a scan; skip it when prefix matches already fill the page.
  if (hits.length < limit) {
    for (let i = 0; i < ix.folded.length; i++) {
      if (!seen.has(i) && ix.folded[i].includes(qf) && allowed(i)) hits.push({ i, rank: 2 });
    }
  }
  const F = ix.folded;
  hits.sort((a, b) => a.rank - b.rank || F[a.i].length - F[b.i].length || (F[a.i] < F[b.i] ? -1 : F[a.i] > F[b.i] ? 1 : 0) || (ix.ids[a.i] < ix.ids[b.i] ? -1 : 1));
  return hits.slice(0, limit).map(({ i, rank }) => ({ id: ix.ids[i], kind: ix.kinds[i], title: ix.titles[i], rank }));
}
